import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { projectLocal } from '~/data/local';
import { projectRemote } from '~/data/remote';
import { Project } from '~/types';
import store from '~/store';
import {
  getFavoriteProjectIds,
  getLastUsedProjectIds,
} from '~/sqlite/lastUsedCache';

function cordovaFileBaseDir(): string {
  const dataDirectory = window['cordova']?.file?.dataDirectory;
  return dataDirectory ? `${dataDirectory}/` : '';
}

function mapStoredProject(project: Project): Project {
  const baseDir = cordovaFileBaseDir();
  return {
    ...project,
    image: project.image ? `${baseDir}${project.image}` : '',
  };
}

export class ProjectRepository {
  async getProject(id: Project['id']): Promise<Project | undefined> {
    if (connectivityService.isOnline()) {
      const data = await projectRemote.fetchProject(id);
      if (store.getState().projects.isOfflineAvailable(id)) {
        await projectLocal.upsertProject(data);
      }
      return data;
    }
    const local = await projectLocal.projectDetails(id);
    return local ? mapStoredProject(local) : undefined;
  }

  /** After online favorites fetch — persist SQLite cache for offline lists. */
  async syncFavoritesCache(favorites: Project[]): Promise<void> {
    let lastUsed: Project[] = [];
    try {
      lastUsed = await projectRemote.fetchLastUsed();
    } catch {
      lastUsed = [];
    }

    await projectLocal.reset(favorites);
    const favoriteIds = new Set(favorites.map((project) => String(project.id)));
    const nonFavoriteLastUsed = lastUsed.filter(
      (project) => !favoriteIds.has(String(project.id))
    );
    for (const project of nonFavoriteLastUsed) {
      await projectLocal.upsertProject(project);
    }
    return;
  }

  async getOfflineFavorites(): Promise<Project[]> {
    const projects = await projectLocal.getAll();
    if (!projects) {
      return [];
    }
    const favoriteIds = getFavoriteProjectIds();
    const rows =
      favoriteIds === null || favoriteIds.length === 0
        ? []
        : projects.filter((project) => favoriteIds.includes(String(project.id)));
    return rows.map(mapStoredProject);
  }

  async getOfflineLastUsed(): Promise<Project[]> {
    const lastUsedIds = getLastUsedProjectIds();
    const projects = await projectLocal.getAll();
    if (!projects) {
      return [];
    }
    const byId = new Map(projects.map((project) => [String(project.id), project]));
    return lastUsedIds.reduce((list, id) => {
      const project = byId.get(id);
      if (project) {
        list.push(mapStoredProject(project));
      }
      return list;
    }, [] as Project[]);
  }

  async cacheOpenedProject(project: Project): Promise<void> {
    if (!store.getState().projects.isOfflineAvailable(project.id)) {
      return;
    }
    await projectLocal.upsertProject(project);
  }
}

export const projectRepository = new ProjectRepository();
